/**
 * Minting a `SurveyDocument`.
 *
 * Lives in core/ rather than beside the screen that first needed it because TWO
 * layers mint documents now, and a second copy of this shape would be free to
 * drift from the first:
 *
 *   the UI  — "+ New Survey", the field-initiated path (spec §2.1's exception).
 *   the DB  — the assignment inbox in src/db/sync.ts, which has to turn an
 *             office assignment that carries nothing but an address into a
 *             document the device can open (spec §2.1's usual path).
 *
 * Pure: no clock, no id source, no store. Every varying input is a parameter, so
 * a test can produce byte-identical documents.
 */

import { SCHEMA_VERSION } from './types';
import type { SurveyDocument, SurveyOrigin, SurveyStatus } from './types';

export interface NewSurveyInput {
  surveyId: string;
  origin: SurveyOrigin;
  /** ISO-8601 instant. Passed in — this module reads no clock. */
  createdAt: string;
  /** The surveyor's registry id. Absent on a device that has never seen /api/me. */
  createdBy?: string;
  assignedTo?: string;
  status?: SurveyStatus;
}

/**
 * An empty but fully valid `SurveyDocument`.
 *
 * "Fully valid" matters: this document is written to IndexedDB immediately and
 * pushed to `PUT /api/surveys/:id` on the next drain, where `SurveyDocumentSchema`
 * parses it. Every required field is therefore present from the first instant —
 * an empty answer map and an empty room list are legal (brief §5: an assignment
 * can arrive as thin as an address), a missing `adHoc` block is not.
 */
export function emptySurveyDocument(input: NewSurveyInput): SurveyDocument {
  return {
    schemaVersion: SCHEMA_VERSION,
    surveyId: input.surveyId,
    origin: input.origin,
    // A survey the surveyor started on site is already underway; 'assigned'
    // would describe a queue it never sat in (mirrors POST /api/surveys).
    status: input.status ?? (input.origin === 'field_initiated' ? 'in_progress' : 'assigned'),
    ...(input.assignedTo ? { assignedTo: input.assignedTo } : {}),
    ...(input.createdBy ? { createdBy: input.createdBy } : {}),
    createdAt: input.createdAt,
    updatedAt: input.createdAt,
    building: { id: `bld-${input.surveyId}` },
    rooms: [],
    adHoc: {
      storageRental: false,
      dumpsterRental: false,
      cleanPitOfOilAndDebris: { flag: false, count: 0 },
      parking: false,
      // `other` is no longer written by the phone (task 7) — the free-form
      // "Something else" items and their photo picker are gone. The field
      // stays required and seeded empty so an old, already-populated record
      // still parses under this same schema.
      other: [],
    },
    answers: {},
    notes: [],
    photos: [],
    gaps: [],
  };
}
